import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Plus, Calendar, Power, Wifi, WifiOff, RefreshCw, Download, AlertCircle, CheckCircle, Bell, CheckCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import syncService from '../services/syncService';
import useInstallPrompt from '../hooks/useInstallPrompt';
import { useNotifications } from '../context/NotificationsContext';
import { formatPakistanDate, formatPakistanTime } from '../utils/pakistanTime';

const pageTitles = {
  '/medicines/new':    'Add New Medicine',
  '/refunds/customer': 'Customer Refunds',
  '/refunds/supplier': 'Supplier Refunds',
  '/refunds':          'Refunds',
  '/accounting':       'Accounting',
  '/backup':           'Backup',
  '/pos':              'POS / New Sale',
  '/sales':            'Sales History',
  '/daily-closing':    'Daily Closing',
  '/monthly-closing':  'Monthly Closing',
  '/customers':        'Customers',
  '/medicines':        'Medicines',
  '/expiry-alerts':    'Expiry Alerts',
  '/low-stock':        'Low Stock',
  '/damage-alerts':    'Damage Alerts',
  '/categories':       'Categories',
  '/suppliers':        'Suppliers',
  '/purchases':        'Purchases',
  '/expenses':         'Expenses',
  '/staff':            'Staff',
  '/users':            'System Users',
  '/reports':          'Reports',
  '/settings':         'Settings',
  '/ai-assistant':     'AI Assistant',
  '/':                 'Dashboard',
};

export default function Header({ onMenuToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { install, canInstall } = useInstallPrompt();
  const [isOnline, setIsOnline] = useState(syncService.isOnline);
  const [pendingSync, setPendingSync] = useState(syncService.pendingCount);
  const [syncing, setSyncing] = useState(syncService.isSyncing);
  const [syncStatus, setSyncStatus] = useState(syncService.syncStatus || 'idle');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef(null);
  const { notifications, counts, markRead, markAllRead } = useNotifications();

  useEffect(() => {
    const unsubscribe = syncService.subscribe((state) => {
      setIsOnline(state.isOnline);
      setPendingSync(state.pendingCount);
      setSyncing(state.isSyncing);
      setSyncStatus(state.syncStatus || 'idle');
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!notificationsOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [notificationsOpen]);

  const handleSync = async () => {
    await syncService.syncAll();
  };

  const pageTitle =
    Object.entries(pageTitles).find(([path]) =>
      path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
    )?.[1] || '';

  const today = formatPakistanDate(new Date(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const displayName = user?.full_name || 'Shop Owner';
  const displayRole = user?.role || 'Owner';
  const displayInitial = displayName.charAt(0).toUpperCase() || 'S';

  const getSyncStatusIcon = () => {
    if (syncStatus === 'success') return <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />;
    if (syncStatus === 'error') return <AlertCircle className="h-3.5 w-3.5 text-red-600" />;
    if (syncing) return <RefreshCw className="h-3.5 w-3.5 animate-spin" />;
    return <RefreshCw className="h-3.5 w-3.5" />;
  };

  const getSyncStatusColor = () => {
    if (syncStatus === 'success') return 'bg-emerald-50 text-emerald-700';
    if (syncStatus === 'error') return 'bg-red-50 text-red-700';
    return 'bg-amber-50 text-amber-700';
  };

  const getSyncTooltip = () => {
    if (syncStatus === 'success') return `Last synced: ${syncService.lastSyncedAt ? formatPakistanTime(syncService.lastSyncedAt) : 'Just now'}`;
    if (syncStatus === 'error') return 'Sync failed. Click to retry.';
    if (syncing) return 'Syncing...';
    if (pendingSync > 0) return `${pendingSync} pending changes. Click to sync.`;
    return 'All synced';
  };

  const notificationPath = (type) => ({ EXPIRY: '/expiry-alerts', LOW_STOCK: '/low-stock', DAMAGE: '/damage-alerts' }[type] || '/medicines');
  const isCritical = (type, title) => {
    if (type === 'DAMAGE') return true;
    if (type === 'EXPIRY' && (title === 'EXPIRED' || title === 'Medicine Expired')) return true;
    if (type === 'LOW_STOCK' && (title === 'OUT OF STOCK' || title === 'Out of Stock')) return true;
    return false;
  };
  const notificationColor = (type, title) => {
    if (isCritical(type, title)) return 'text-red-600 bg-red-50';
    if (type === 'EXPIRY') return 'text-amber-600 bg-amber-50';
    return 'text-orange-600 bg-orange-50';
  };
  const notificationDotColor = (type, title) => {
    if (isCritical(type, title)) return 'bg-red-500';
    if (type === 'EXPIRY') return 'bg-amber-500';
    return 'bg-orange-500';
  };
  const notificationUnreadBg = (type, title) => {
    if (isCritical(type, title)) return 'bg-red-50/50';
    if (type === 'EXPIRY') return 'bg-amber-50/50';
    return 'bg-orange-50/50';
  };
  const relativeTime = (value) => {
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return new Date(value).toLocaleDateString();
  };

  return (
    <header className="flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-4 lg:px-6">
      {/* Mobile hamburger */}
      <button
        onClick={onMenuToggle}
        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 lg:hidden"
        aria-label="Toggle menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Page title */}
      {pageTitle ? <h1 className="text-lg font-bold text-gray-900 lg:text-xl">{pageTitle}</h1> : null}

      {/* Spacer */}
      <div className="flex-1" />

      {/* ── Online/Offline Status ── */}
      <div className="flex items-center gap-2">
        {isOnline ? (
          <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
            <Wifi className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Online</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            <WifiOff className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Offline</span>
          </div>
        )}
        {syncing && (
          <div
            className="flex items-center gap-1.5 rounded-lg bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700"
            role="status"
            aria-live="polite"
            title="Downloading the latest data for offline use"
          >
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            <span className="hidden sm:inline">Syncing offline data…</span>
          </div>
        )}
      </div>

      {/* ── Date picker style ── */}
      <div className="hidden items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 sm:flex">
        <Calendar className="h-4 w-4 text-gray-400" />
        <span className="font-medium">{today}</span>
      </div>

      {/* ── Install App Button ── */}
      {canInstall && (
        <button
          onClick={install}
          className="hidden items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-100 hover:shadow-md active:scale-[0.97] sm:inline-flex"
          title="Install Gaza Pharmacy as an app"
        >
          <Download className="h-4 w-4" />
          Install App
        </button>
      )}

      {/* ── New Sale CTA ── */}
      <Link
        to="/pos"
        className="hidden items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 hover:shadow-md active:scale-[0.97] sm:inline-flex"
      >
        <Plus className="h-4 w-4" />
        New Sale
      </Link>

      {/* ── Notifications ── */}
      <div ref={notificationsRef} className="relative">
        <button
          onClick={() => setNotificationsOpen((open) => !open)}
          className="relative rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Notifications"
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {counts.unread > 0 && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />}
        </button>
        {notificationsOpen && (
          <div className="absolute right-0 top-12 z-50 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div><p className="text-sm font-bold text-gray-900">Notifications</p><p className="text-[11px] text-gray-500">{counts.unread} unread alerts</p></div>
              {counts.unread > 0 && <button onClick={markAllRead} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>}
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
              {notifications.length ? notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={async () => { if (!notification.is_read) await markRead(notification.id); setNotificationsOpen(false); navigate(notificationPath(notification.type)); }}
                  className={`flex w-full gap-3 px-4 py-3 text-left transition hover:bg-gray-50 ${notification.is_read ? 'bg-white' : notificationUnreadBg(notification.type, notification.title)}`}
                >
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${notificationColor(notification.type, notification.title)}`}><AlertTriangle className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1"><span className="flex items-start justify-between gap-2"><strong className="text-xs text-gray-900">{notification.title}</strong><time className="shrink-0 text-[10px] text-gray-400">{relativeTime(notification.created_at)}</time></span><span className="mt-0.5 block text-xs leading-5 text-gray-600">{notification.message}</span></span>
                  {!notification.is_read && <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${notificationDotColor(notification.type, notification.title)}`} />}
                </button>
              )) : <div className="px-5 py-10 text-center text-sm text-gray-400">No alerts right now.</div>}
            </div>
          </div>
        )}
      </div>

      {/* ── User Avatar ── */}
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white uppercase">
          {displayInitial}
        </div>
        <div className="hidden text-left sm:block">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{displayName}</p>
          <p className="text-[10px] font-medium text-gray-400 uppercase leading-tight capitalize">{displayRole}</p>
        </div>
      </div>

      {/* ── Power / Logout ── */}
      <button
        onClick={handleLogout}
        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        aria-label="Logout"
        title="Sign out"
      >
        <Power className="h-5 w-5" />
      </button>
    </header>
  );
}
